/**
 * @file apps/web/src/pages/ClosePage.tsx
 * @description Project close-out page: approval routing and sign-off management.
 * Shows pending approvals, sign-off checklist, and final project closure controls.
 * Route: /projects/:projectId/close
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle, CheckCircle, Clock, XCircle, Plus, Shield,
  FileSignature, ChevronDown, ChevronUp, Users,
} from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { approvalsApi, signoffApi, projectsApi, testScriptsApi, uatApi, documentsApi } from '../lib/api.js';
import { ApprovalStepper, type ApprovalStep } from '../components/ApprovalStepper.js';
import { AuditTrail, type AuditEvent } from '../components/AuditTrail.js';
import { RoutingBuilder, type RoutingStep, type WorkflowType } from '../components/RoutingBuilder.js';
import { Badge } from '../components/Badge.js';
import { SubmitForApprovalPanel } from '../components/SubmitForApprovalPanel.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Approval {
  id:            string;
  status:        string;
  workflow_type: WorkflowType;
  entity_type:   string;
  entity_id:     string;
  created_at:    string;
  steps?:        ApprovalStep[];
}

interface Signoff {
  id:          string;
  entity_type: string;
  entity_id:   string;
  signed_by:   string;
  signed_at:   string;
  signature?:  string | null;
  notes?:      string | null;
}

interface TeamMember {
  id:         string;
  full_name?: string | null;
  email:      string;
}

interface EntityOption {
  id:    string;
  label: string;
}

const ENTITY_TYPES = [
  { value: 'project',     label: 'Project' },
  { value: 'sign_off',    label: 'Sign-off' },
  { value: 'test_script', label: 'Test Script' },
  { value: 'uat_session', label: 'UAT Session' },
  { value: 'document',    label: 'Document' },
] as const;

function entitySelectLabel(type: string): string {
  return {
    project:     'Select Project',
    sign_off:    'Select Sign-off',
    test_script: 'Select Test Script',
    uat_session: 'Select Session',
    document:    'Select Document',
  }[type] ?? 'Select Entity';
}

// ---------------------------------------------------------------------------
// Approval card (compact)
// ---------------------------------------------------------------------------

function ApprovalCard({
  approval,
  expanded,
  onToggle,
  onDecide,
  onCancel,
  auditEvents,
  auditLoading,
  currentUserId,
}: {
  approval:      Approval;
  expanded:      boolean;
  onToggle:      () => void;
  onDecide:      (stepId: string, decision: 'approved' | 'rejected') => Promise<void>;
  onCancel:      (id: string, reason: string) => Promise<void>;
  auditEvents:   AuditEvent[];
  auditLoading:  boolean;
  currentUserId: string;
}) {
  const statusIcon = {
    pending:   <Clock size={13} className="text-warning" />,
    approved:  <CheckCircle size={13} className="text-success" />,
    rejected:  <XCircle size={13} className="text-danger" />,
    cancelled: <XCircle size={13} className="text-muted" />,
  }[approval.status] ?? <Clock size={13} className="text-muted" />;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-sky/20 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {statusIcon}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink truncate">
              {approval.entity_type.replace('_', ' ')} approval
            </p>
            <p className="text-[11px] font-mono text-muted mt-0.5">
              {approval.workflow_type} · {new Date(approval.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={approval.status as never} size="xs" />
          {expanded ? <ChevronUp size={14} className="text-muted" /> : <ChevronDown size={14} className="text-muted" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-4 space-y-4">
          <ApprovalStepper
            steps={approval.steps ?? []}
            workflowType={approval.workflow_type}
            currentUserId={currentUserId}
            onDecide={onDecide}
          />
          <div>
            <p className="label mb-2">Audit Trail</p>
            <AuditTrail events={auditEvents} loading={auditLoading} />
          </div>
          {!['approved', 'rejected', 'cancelled'].includes(approval.status) && (
            <div className="flex justify-end">
              <button
                onClick={async () => {
                  const reason = prompt('Reason for cancellation (optional):') ?? '';
                  await onCancel(approval.id, reason);
                }}
                className="btn-ghost text-sm text-danger hover:bg-danger/8"
              >
                <XCircle size={14} /> Cancel Workflow
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New approval form
// ---------------------------------------------------------------------------

function NewApprovalForm({
  teamMembers,
  projectId,
  onCreated,
  onCancel,
}: {
  teamMembers: TeamMember[];
  projectId:   string;
  onCreated:   (a: Approval) => void;
  onCancel:    () => void;
}) {
  const { user, devHeaders } = useAuth();
  const [steps, setSteps]           = useState<RoutingStep[]>([]);
  const [wfType, setWfType]         = useState<WorkflowType>('sequential');
  const [entityType, setEntityType] = useState('project');
  const [entityId, setEntityId]     = useState('');
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr]               = useState('');

  // Load entity options whenever entity type changes
  useEffect(() => {
    let cancelled = false;
    setEntityId('');
    setEntityOptions([]);
    setEntityLoading(true);

    const load = async (): Promise<EntityOption[]> => {
      if (entityType === 'project') {
        const data = await projectsApi.list(user.tenantId, devHeaders) as { id: string; name: string }[];
        return data.map((p) => ({ id: p.id, label: p.name }));
      }
      if (entityType === 'test_script') {
        const data = await testScriptsApi.list(projectId, devHeaders) as { id: string; title: string }[];
        return data.map((s) => ({ id: s.id, label: s.title }));
      }
      if (entityType === 'uat_session') {
        const data = await uatApi.listSessions(projectId, devHeaders) as { id: string; name: string }[];
        return data.map((s) => ({ id: s.id, label: s.name }));
      }
      if (entityType === 'document') {
        const data = await documentsApi.list(projectId, devHeaders) as { id: string; title: string }[];
        return data.map((d) => ({ id: d.id, label: d.title }));
      }
      // sign_off: no listable endpoint — fall back to project scope
      const data = await projectsApi.list(user.tenantId, devHeaders) as { id: string; name: string }[];
      return data.map((p) => ({ id: p.id, label: p.name }));
    };

    load()
      .then((opts) => { if (!cancelled) { setEntityOptions(opts); setEntityLoading(false); } })
      .catch(() => { if (!cancelled) setEntityLoading(false); });

    return () => { cancelled = true; };
  }, [entityType, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (steps.length === 0) { setErr('Add at least one approver.'); return; }
    if (!entityId)          { setErr('Please select an entity.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      const created = await approvalsApi.create({
        project_id:    projectId,
        requested_by:  user.userId,
        entity_type:   entityType,
        entity_id:     entityId,
        workflow_type: wfType,
        steps: steps.map((s, i) => ({
          approver_id:    s.approverId,
          step_order:     i + 1,
          parallel_group: s.parallelGroup,
          condition_expr: s.conditionExpr ?? null,
          is_required:    s.isRequired,
        })),
      }, devHeaders) as Approval;
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create approval');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">New Approval Workflow</h3>
        <button onClick={onCancel} className="btn-ghost text-xs text-muted">Cancel</button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label mb-1.5">Entity Type</label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="select text-sm"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label mb-1.5">{entitySelectLabel(entityType)}</label>
          <select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className="select text-sm"
            disabled={entityLoading}
          >
            <option value="">
              {entityLoading ? 'Loading…' : `— choose ${ENTITY_TYPES.find((t) => t.value === entityType)?.label ?? 'entity'} —`}
            </option>
            {entityOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <RoutingBuilder
        teamMembers={teamMembers}
        value={steps}
        workflowType={wfType}
        onChange={(s, t) => { setSteps(s); setWfType(t); }}
      />

      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-[rgba(8,17,31,0.06)]">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="btn-primary text-sm"
        >
          {submitting ? 'Creating…' : 'Create Workflow'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signoff panel
// ---------------------------------------------------------------------------

function SignoffPanel({
  signoffs,
  loading,
  uatPhaseApproval,
  recording,
  onRequestApproval,
  onRecordSignoff,
}: {
  signoffs:          Signoff[];
  loading:           boolean;
  uatPhaseApproval:  { id: string; status: string } | null;
  recording:         boolean;
  onRequestApproval: () => void;
  onRecordSignoff:   () => void;
}) {
  if (loading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[1,2].map((i) => <div key={i} className="card h-14 bg-gray/60" />)}
      </div>
    );
  }

  const approvalApproved = uatPhaseApproval?.status === 'approved';

  return (
    <div className="space-y-2">
      {signoffs.length === 0 ? (
        <div className="empty-state py-8">
          <FileSignature size={24} className="text-muted/40 mb-2 mx-auto" />
          <p className="text-sm text-muted">No sign-offs yet.</p>
          {(!uatPhaseApproval || ['rejected', 'cancelled'].includes(uatPhaseApproval.status)) ? (
            <button onClick={onRequestApproval} className="btn-secondary mt-3">
              <Plus size={14} /> Request Sign-off
            </button>
          ) : approvalApproved ? (
            <button onClick={onRecordSignoff} disabled={recording} className="btn-primary mt-3">
              {recording ? 'Recording…' : 'Record Final Sign-off'}
            </button>
          ) : (
            <p className="text-xs font-mono text-muted mt-3">
              UAT Phase Sign-off approval is {uatPhaseApproval.status} — see Approval Workflows.
            </p>
          )}
        </div>
      ) : (
        <>
          {signoffs.map((s) => (
            <div key={s.id} className="card p-3 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <CheckCircle size={14} className="text-success shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {s.entity_type.replace('_', ' ')} sign-off
                  </p>
                  <p className="text-[11px] font-mono text-muted mt-0.5">
                    {new Date(s.signed_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </p>
                </div>
              </div>
              <Badge variant="approved" size="xs" />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClosePage
// ---------------------------------------------------------------------------

export function ClosePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, devHeaders } = useAuth();

  const [approvals, setApprovals]     = useState<Approval[]>([]);
  const [signoffs, setSignoffs]       = useState<Signoff[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showNew, setShowNew]         = useState(false);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [auditMap, setAuditMap]       = useState<Record<string, AuditEvent[]>>({});
  const [auditLoading, setAuditLoading] = useState<Record<string, boolean>>({});
  const [uatPhaseApproval, setUatPhaseApproval] = useState<{ id: string; status: string } | null>(null);
  const [showRequestSignoffPanel, setShowRequestSignoffPanel] = useState(false);
  const [recordingSignoff, setRecordingSignoff] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [apprs, soffs, members, uatApproval] = await Promise.all([
        approvalsApi.list(projectId, devHeaders) as Promise<Approval[]>,
        signoffApi.list(projectId, devHeaders) as Promise<Signoff[]>,
        projectsApi.members(projectId, devHeaders) as Promise<TeamMember[]>, // project members only: POST /approvals rejects anyone else
        approvalsApi.forEntity('uat_phase_signoff', projectId, devHeaders) as Promise<{ id: string; status: string } | null>,
      ]);
      setApprovals(apprs);
      setSignoffs(soffs);
      setTeamMembers(members);
      setUatPhaseApproval(uatApproval);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load close-out data');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Load audit log when expanding an approval
  const handleToggle = (id: string) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id;
      if (next && !auditMap[next]) {
        setAuditLoading((m) => ({ ...m, [next]: true }));
        approvalsApi.getAudit(next, devHeaders)
          .then((data) => setAuditMap((m) => ({ ...m, [next]: data as AuditEvent[] })))
          .catch(() => setAuditMap((m) => ({ ...m, [next]: [] })))
          .finally(() => setAuditLoading((m) => ({ ...m, [next]: false })));
      }
      return next;
    });
  };

  const handleDecide = async (approvalId: string, stepId: string, decision: 'approved' | 'rejected') => {
    await approvalsApi.decide(approvalId, stepId, { decision }, devHeaders);
    const updated = await approvalsApi.get(approvalId, devHeaders) as Approval;
    setApprovals((prev) => prev.map((a) => a.id === approvalId ? updated : a));
    const events = await approvalsApi.getAudit(approvalId, devHeaders) as AuditEvent[];
    setAuditMap((m) => ({ ...m, [approvalId]: events }));
  };

  const handleCancel = async (id: string, reason: string) => {
    await approvalsApi.cancel(id, reason, devHeaders);
    const updated = await approvalsApi.get(id, devHeaders) as Approval;
    setApprovals((prev) => prev.map((a) => a.id === id ? updated : a));
  };

  // Stage 2 — record the formal sign-off once the UAT phase approval
  // (Stage 1, requested via SubmitForApprovalPanel below) is approved.
  // Reuses signoff.ts's existing approvalId-must-be-approved validation
  // as-is — no new backend logic.
  //
  // NOTE: field name is `approvalId` (camelCase), NOT `approval_id`. Unlike
  // most other fields on this endpoint, signoff.ts's POST /signoffs body
  // parser only reads `b.approvalId` — there is no snake_case alias for
  // this one field (see apps/api/src/routes/signoff.ts). Sending
  // `approval_id` would silently be ignored, leaving approvalId null on
  // the server and skipping the "approval must be approved" gate entirely.
  const handleRecordSignoff = async () => {
    if (!projectId || !uatPhaseApproval) return;
    setRecordingSignoff(true);
    try {
      const created = await signoffApi.create({
        project_id:  projectId,
        entity_type: 'project',
        entity_id:   projectId,
        approvalId:  uatPhaseApproval.id,
        signed_by:   user.userId,
      }, devHeaders) as Signoff;
      setSignoffs((prev) => [created, ...prev]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create sign-off');
    } finally {
      setRecordingSignoff(false);
    }
  };

  // Derived
  const pendingApprovals  = approvals.filter((a) => !['approved', 'rejected', 'cancelled'].includes(a.status));
  const approvedApprovals = approvals.filter((a) => a.status === 'approved');
  const allApproved       = approvals.length > 0 && pendingApprovals.length === 0;

  return (
    <div className="p-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Close-out</h1>
          <p className="text-sm text-muted mt-1">
            Approval routing and sign-off management
          </p>
        </div>
        {!showNew && (
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm">
            <Plus size={14} /> New Approval
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* All-approved banner */}
      {!loading && allApproved && (
        <div className="card p-4 mb-4 flex items-center gap-3 border-success/20 bg-success/6">
          <CheckCircle size={18} className="text-success shrink-0" />
          <div>
            <p className="text-sm font-semibold text-success">All approvals complete</p>
            <p className="text-xs text-muted mt-0.5">
              {approvedApprovals.length} workflow{approvedApprovals.length !== 1 ? 's' : ''} approved. Ready for final sign-off.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-4 animate-pulse">
          <div className="card h-32 bg-gray/60" />
          <div className="card h-48 bg-gray/60" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
          {/* Left: Approvals */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold text-ink flex items-center gap-2">
                <Shield size={14} />
                Approval Workflows
                {pendingApprovals.length > 0 && (
                  <span className="text-xs font-mono px-1.5 py-0.5 rounded-full bg-warning/10 text-warning">
                    {pendingApprovals.length} pending
                  </span>
                )}
              </h2>
            </div>

            {/* New approval form */}
            {showNew && (
              <NewApprovalForm
                teamMembers={teamMembers}
                projectId={projectId ?? ''}
                onCreated={(a) => {
                  setApprovals((prev) => [a, ...prev]);
                  setShowNew(false);
                }}
                onCancel={() => setShowNew(false)}
              />
            )}

            {approvals.length === 0 && !showNew ? (
              <div className="empty-state py-10">
                <Shield size={24} className="text-muted/40 mb-2 mx-auto" />
                <p className="text-sm text-muted">No approval workflows yet.</p>
                <button onClick={() => setShowNew(true)} className="btn-secondary mt-3">
                  <Plus size={14} /> Create First Workflow
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {approvals.map((a) => (
                  <ApprovalCard
                    key={a.id}
                    approval={a}
                    expanded={expandedId === a.id}
                    onToggle={() => handleToggle(a.id)}
                    onDecide={(stepId, decision) => handleDecide(a.id, stepId, decision)}
                    onCancel={handleCancel}
                    auditEvents={auditMap[a.id] ?? []}
                    auditLoading={auditLoading[a.id] ?? false}
                    currentUserId={user.userId}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: Sign-offs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold text-ink flex items-center gap-2">
                <FileSignature size={14} />
                Sign-offs
              </h2>
            </div>

            {/* Team summary */}
            <div className="card p-3">
              <div className="flex items-center gap-2 mb-2">
                <Users size={13} className="text-muted" />
                <p className="text-xs font-medium text-ink">Project Team</p>
              </div>
              <div className="space-y-1.5">
                {teamMembers.slice(0, 4).map((m) => (
                  <div key={m.id} className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-mono font-bold shrink-0"
                      style={{ backgroundColor: 'var(--blue)' }}
                    >
                      {(m.full_name ?? m.email).slice(0, 1).toUpperCase()}
                    </div>
                    <p className="text-xs text-ink truncate">{m.full_name ?? m.email}</p>
                  </div>
                ))}
                {teamMembers.length > 4 && (
                  <p className="text-[11px] font-mono text-muted pl-7">
                    +{teamMembers.length - 4} more
                  </p>
                )}
              </div>
            </div>

            {showRequestSignoffPanel && projectId && (
              <div className="mb-3">
                <SubmitForApprovalPanel
                  projectId={projectId}
                  entityType="uat_phase_signoff"
                  entityId={projectId}
                  title="UAT Phase Sign-off"
                  onCreated={(created) => {
                    setUatPhaseApproval(created as typeof uatPhaseApproval);
                    setShowRequestSignoffPanel(false);
                  }}
                  onCancel={() => setShowRequestSignoffPanel(false)}
                />
              </div>
            )}

            <SignoffPanel
              signoffs={signoffs}
              loading={false}
              uatPhaseApproval={uatPhaseApproval}
              recording={recordingSignoff}
              onRequestApproval={() => setShowRequestSignoffPanel(true)}
              onRecordSignoff={() => void handleRecordSignoff()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
